import React, { useState, useEffect } from 'react';
import api, { formatApiError } from '../api/client';
import { ChevronRight, ChevronLeft, Check, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { isValidPhone } from '../lib/validate';

const STEPS = [
  { key: 'basic', title: 'Basic Details' },
  { key: 'education', title: 'Education & Skills' },
  { key: 'experience', title: 'Experience' },
  { key: 'salary', title: 'Salary' },
  { key: 'additional', title: 'Additional Info' },
];

const QUAL = ['10th', '12th', 'SSC', 'Graduate', 'Post Graduate', 'Diploma', 'Uneducated', 'Other'];
const SOFTWARE = ['MS Word', 'MS Excel', 'MS PowerPoint', 'CorelDRAW', 'Photoshop', 'Tally', 'SAP'];

export default function CandidateKiosk() {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({
    applied_post: '',
    name: '',
    dob: '',
    phone: '',
    home_phone: '',
    marital_status: '',
    father_husband_name: '',
    address: '',
    qualification: '',
    computer_knowledge: false,
    typing_hindi: false,
    typing_english: false,
    software_skills: [],
    previous_employer: '',
    experience_years: '',
    current_employer: '',
    reference_name: '',
    reference_phone: '',
    current_salary: '',
    expected_salary: '',
    reason_for_leaving: '',
    referred_by: '',
    strengths: '',
    weaknesses: '',
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // Pre-fill name & phone when redirected from the guard portal (/apply?name=...&phone=...)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const name  = p.get('name')  || '';
    const phone = p.get('phone') || '';
    if (name || phone) setForm((prev) => ({ ...prev, name, phone }));
  }, []);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!isValidPhone(form.phone)) {
      setErr('Enter a valid 10-digit Indian mobile number for Phone');
      return;
    }
    if (form.home_phone && !isValidPhone(form.home_phone)) {
      setErr('Home Phone: Enter a valid 10-digit Indian mobile number');
      return;
    }
    if (form.reference_phone && !isValidPhone(form.reference_phone)) {
      setErr('Reference Phone: Enter a valid 10-digit Indian mobile number');
      return;
    }
    setErr('');
    setLoading(true);
    try {
      const payload = {
        ...form,
        experience_years: form.experience_years ? parseFloat(form.experience_years) : null,
        current_salary: form.current_salary ? parseFloat(form.current_salary) : null,
        expected_salary: form.expected_salary ? parseFloat(form.expected_salary) : null,
      };
      const { data } = await api.post('/candidates', payload);
      setSubmitted(data);
      toast.success('Application submitted — thank you!');
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail, e?.response?.data));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-50">
        <div className="card p-10 max-w-md w-full text-center shadow-hard">
          <div className="w-14 h-14 bg-green-500 text-white flex items-center justify-center rounded-sm mx-auto mb-5"><Check size={32} /></div>
          <h1 className="heading text-3xl font-black text-ink">Application received</h1>
          <p className="text-sm text-zinc-600 mt-3">Thanks, {submitted.name}. HR will reach out if your profile matches an opening.</p>
          <button className="btn-primary mt-6" onClick={() => window.location.reload()}>Submit another</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="kiosk">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="Unikin" className="w-9 h-9 object-contain" />
          <div>
            <div className="heading font-black text-ink"><span className="lowercase">unikin</span> · Kiosk</div>
            <div className="overline text-[10px]">Employment Application</div>
          </div>
        </div>
        <a href="/login" className="btn-ghost text-xs">Staff Login →</a>
      </header>

      <div className="max-w-3xl mx-auto p-6">
        {/* Progress */}
        <div className="flex items-center justify-between mb-8" data-testid="step-indicator">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-sm flex items-center justify-center text-xs font-bold ${
                  i < step ? 'bg-green-500 text-white' : i === step ? 'bg-primary text-white' : 'bg-white border border-zinc-300 text-zinc-400'
                }`} data-testid={`step-${i}`}>
                  {i < step ? <Check size={14} /> : i + 1}
                </div>
                <div className={`text-[10px] uppercase font-bold tracking-wider mt-1 ${i === step ? 'text-primary' : 'text-zinc-500'}`}>{s.title.split(' ')[0]}</div>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? 'bg-green-500' : 'bg-zinc-300'}`} />}
            </div>
          ))}
        </div>

        <div className="card p-6 md:p-8">
          <div className="overline mb-1">Step {step + 1} of {STEPS.length}</div>
          <h2 className="heading text-2xl font-black text-ink mb-6">{STEPS[step].title}</h2>

          {step === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="step-basic">
              <Field label="Applied for Post *" required>
                <input className="input" required value={form.applied_post} onChange={(e) => update({ applied_post: e.target.value })} data-testid="k-post" />
              </Field>
              <Field label="Full Name *" required>
                <input className="input" required value={form.name} onChange={(e) => update({ name: e.target.value })} data-testid="k-name" />
              </Field>
              <Field label="Date of Birth">
                <input type="date" className="input" value={form.dob} onChange={(e) => update({ dob: e.target.value })} />
              </Field>
              <Field label="Phone *" required>
                <input className="input" required value={form.phone} onChange={(e) => update({ phone: e.target.value })} data-testid="k-phone" />
              </Field>
              <Field label="Home Phone">
                <input className="input" value={form.home_phone} onChange={(e) => update({ home_phone: e.target.value })} />
              </Field>
              <Field label="Marital Status">
                <select className="input" value={form.marital_status} onChange={(e) => update({ marital_status: e.target.value })}>
                  <option value="">—</option>
                  {['Single', 'Married', 'Separated', 'Other'].map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Father / Husband Name">
                <input className="input" value={form.father_husband_name} onChange={(e) => update({ father_husband_name: e.target.value })} />
              </Field>
              <Field label="Address" full>
                <textarea rows={2} className="input" value={form.address} onChange={(e) => update({ address: e.target.value })} />
              </Field>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5" data-testid="step-education">
              <Field label="Qualification">
                <select className="input" value={form.qualification} onChange={(e) => update({ qualification: e.target.value })} data-testid="k-qual">
                  <option value="">—</option>
                  {QUAL.map((q) => <option key={q}>{q}</option>)}
                </select>
              </Field>
              <div className="flex flex-wrap gap-3">
                <ToggleChip checked={form.computer_knowledge} onChange={(v) => update({ computer_knowledge: v })} label="Computer Knowledge" />
                <ToggleChip checked={form.typing_hindi} onChange={(v) => update({ typing_hindi: v })} label="Typing · Hindi" />
                <ToggleChip checked={form.typing_english} onChange={(v) => update({ typing_english: v })} label="Typing · English" />
              </div>
              <div>
                <label className="label">Software Skills</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {SOFTWARE.map((sw) => {
                    const checked = form.software_skills.includes(sw);
                    return (
                      <button
                        key={sw}
                        type="button"
                        onClick={() => update({ software_skills: checked ? form.software_skills.filter((s) => s !== sw) : [...form.software_skills, sw] })}
                        data-testid={`software-${sw.replace(/\s+/g, '-')}`}
                        className={`text-left text-sm py-2 px-3 border rounded-sm ${checked ? 'bg-primary/10 border-primary text-primary font-bold' : 'bg-white border-zinc-300'}`}
                      >
                        {checked ? '✓ ' : ''}{sw}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="step-experience">
              <Field label="Previous Employer"><input className="input" value={form.previous_employer} onChange={(e) => update({ previous_employer: e.target.value })} /></Field>
              <Field label="Experience (years)"><input type="number" step="0.5" className="input" value={form.experience_years} onChange={(e) => update({ experience_years: e.target.value })} /></Field>
              <Field label="Current Employer"><input className="input" value={form.current_employer} onChange={(e) => update({ current_employer: e.target.value })} /></Field>
              <div />
              <Field label="Reference Name"><input className="input" value={form.reference_name} onChange={(e) => update({ reference_name: e.target.value })} /></Field>
              <Field label="Reference Phone"><input className="input" value={form.reference_phone} onChange={(e) => update({ reference_phone: e.target.value })} /></Field>
            </div>
          )}

          {step === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="step-salary">
              <Field label="Current Salary (₹ / month)"><input type="number" className="input" value={form.current_salary} onChange={(e) => update({ current_salary: e.target.value })} /></Field>
              <Field label="Expected Salary (₹ / month)"><input type="number" className="input" value={form.expected_salary} onChange={(e) => update({ expected_salary: e.target.value })} /></Field>
              <Field label="Reason for Leaving" full><textarea rows={2} className="input" value={form.reason_for_leaving} onChange={(e) => update({ reason_for_leaving: e.target.value })} /></Field>
            </div>
          )}

          {step === 4 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="step-additional">
              <Field label="Referred By"><input className="input" value={form.referred_by} onChange={(e) => update({ referred_by: e.target.value })} /></Field>
              <div />
              <Field label="Strengths" full><textarea rows={2} className="input" value={form.strengths} onChange={(e) => update({ strengths: e.target.value })} /></Field>
              <Field label="Weaknesses" full><textarea rows={2} className="input" value={form.weaknesses} onChange={(e) => update({ weaknesses: e.target.value })} /></Field>
              <div className="md:col-span-2 p-4 bg-yellow-50 border border-yellow-200 rounded-sm text-xs text-yellow-800 space-y-1">
                <div className="font-bold uppercase tracking-wider mb-1">Important Notice</div>
                <div>• Documents at joining: Photo, Aadhaar, Bank Passbook copy</div>
                <div>• Notice period: 10 days mandatory</div>
                <div>• No salary for leaving before 30 days</div>
              </div>
            </div>
          )}

          {err && <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-2 rounded-sm mt-4">{err}</div>}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-zinc-200">
            <button onClick={prev} disabled={step === 0} className="btn-outline disabled:opacity-40" data-testid="prev-btn"><ChevronLeft size={14} /> Back</button>
            {step < STEPS.length - 1 ? (
              <button onClick={next} className="btn-primary" data-testid="next-btn">Next <ChevronRight size={14} /></button>
            ) : (
              <button onClick={submit} disabled={loading} className="btn-primary" data-testid="submit-kiosk">
                {loading ? 'Submitting…' : 'Submit Application'} <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children, full }) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <label className="label">{label}{required && ' *'}</label>
      {children}
    </div>
  );
}
function ToggleChip({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`px-3 py-2 text-sm border rounded-sm font-bold uppercase tracking-wider ${checked ? 'bg-primary text-white border-primary' : 'bg-white border-zinc-300'}`}
    >
      {checked ? '✓ ' : ''}{label}
    </button>
  );
}
